import React, { useState, useEffect } from 'react';
import { Cookie, Shield, Settings, X, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';

const CookieConsent = ({ onOpenPrivacyPolicy }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Sempre ativo
    analytics: false,
    marketing: false,
    functionality: false
  });

  useEffect(() => {
    // Verificar se já existe consentimento salvo
    const consent = localStorage.getItem('fernando-tech-cookie-consent');
    if (!consent) {
      // Aguardar 1 segundo para exibir o banner
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (allAccepted = false) => {
    const consentData = {
      timestamp: new Date().toISOString(),
      preferences: allAccepted ? {
        essential: true,
        analytics: true,
        marketing: true,
        functionality: true
      } : preferences,
      version: '1.0'
    };
    localStorage.setItem('fernando-tech-cookie-consent', JSON.stringify(consentData));
    setIsVisible(false);
  };

  const acceptAll = () => {
    setPreferences({
      essential: true,
      analytics: true,
      marketing: true,
      functionality: true
    });
    saveConsent(true);
  };

  const rejectNonEssential = () => {
    setPreferences({
      essential: true,
      analytics: false,
      marketing: false,
      functionality: false
    });
    saveConsent(false);
  };

  const savePreferences = () => {
    saveConsent(false);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Não permite desativar essenciais
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center p-4 pointer-events-none">
      {/* Overlay sutil */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm pointer-events-auto" />
      
      {/* Banner Principal */}
      <div className="relative w-full max-w-4xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-cyan-500/30 rounded-2xl shadow-2xl shadow-cyan-500/20 pointer-events-auto animate-slideUp overflow-hidden">
        {/* Header com ícone */}
        <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 border-b border-cyan-500/20 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-cyan-500/20 p-2 rounded-lg">
              <Cookie className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Aviso de Privacidade e Cookies</h2>
              <p className="text-sm text-cyan-300">FERNANDO TECH - Conformidade LGPD</p>
            </div>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="p-6">
          <p className="text-gray-300 mb-4 leading-relaxed">
            Utilizamos <span className="text-cyan-400 font-semibold">cookies e tecnologias semelhantes</span> para 
            melhorar sua experiência de navegação, analisar o tráfego do site e personalizar conteúdo. 
            Ao clicar em <span className="text-white font-semibold">"Aceitar todos"</span>, você concorda com o uso 
            de cookies conforme nossa Política de Privacidade.
          </p>

          {/* Links */}
          <div className="flex flex-wrap gap-4 mb-6">
            <button
              onClick={onOpenPrivacyPolicy}
              className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2 text-sm font-medium flex items-center gap-1 transition-colors"
            >
              <Shield className="w-4 h-4" />
              Política de Privacidade
            </button>
          </div>

          {/* Configurações expandíveis */}
          <div className="mb-6">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
            >
              <Settings className="w-4 h-4" />
              Configurar preferências de cookies
              {showSettings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showSettings && (
              <div className="mt-4 space-y-3 bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                {/* Cookies Essenciais */}
                <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                  <div>
                    <h4 className="text-white font-medium text-sm">Cookies Essenciais</h4>
                    <p className="text-gray-400 text-xs">Necessários para o funcionamento do site</p>
                  </div>
                  <div className="bg-cyan-500/20 text-cyan-400 px-3 py-1 rounded-full text-xs font-medium">
                    Sempre ativo
                  </div>
                </div>

                {/* Cookies de Analytics */}
                <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                  <div>
                    <h4 className="text-white font-medium text-sm">Cookies de Análise</h4>
                    <p className="text-gray-400 text-xs">Google Analytics, métricas de uso</p>
                  </div>
                  <button
                    onClick={() => togglePreference('analytics')}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      preferences.analytics ? 'bg-cyan-500' : 'bg-slate-600'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      preferences.analytics ? 'translate-x-7' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* Cookies de Marketing */}
                <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                  <div>
                    <h4 className="text-white font-medium text-sm">Cookies de Marketing</h4>
                    <p className="text-gray-400 text-xs">Google Ads, Meta Ads, remarketing</p>
                  </div>
                  <button
                    onClick={() => togglePreference('marketing')}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      preferences.marketing ? 'bg-cyan-500' : 'bg-slate-600'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      preferences.marketing ? 'translate-x-7' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* Cookies de Funcionalidade */}
                <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                  <div>
                    <h4 className="text-white font-medium text-sm">Cookies de Funcionalidade</h4>
                    <p className="text-gray-400 text-xs">Preferências, idioma, personalização</p>
                  </div>
                  <button
                    onClick={() => togglePreference('functionality')}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      preferences.functionality ? 'bg-cyan-500' : 'bg-slate-600'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      preferences.functionality ? 'translate-x-7' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Botões de ação */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={acceptAll}
              className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold py-3 rounded-xl shadow-lg shadow-cyan-500/30 transition-all hover:scale-[1.02]"
            >
              <Check className="w-5 h-5 mr-2" />
              Aceitar todos os cookies
            </Button>
            <Button
              onClick={rejectNonEssential}
              variant="outline"
              className="flex-1 border-2 border-slate-600 text-gray-300 hover:bg-slate-800 hover:text-white font-semibold py-3 rounded-xl transition-all"
            >
              <X className="w-5 h-5 mr-2" />
              Rejeitar não essenciais
            </Button>
            {showSettings && (
              <Button
                onClick={savePreferences}
                variant="outline"
                className="flex-1 border-2 border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 font-semibold py-3 rounded-xl transition-all"
              >
                <Settings className="w-5 h-5 mr-2" />
                Salvar preferências
              </Button>
            )}
          </div>
        </div>

        {/* Footer com informação legal */}
        <div className="bg-slate-900/50 border-t border-slate-700/50 px-6 py-3">
          <p className="text-xs text-gray-500 text-center">
            Em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018) | 
            <span className="text-gray-400"> fernandotech.com.br</span>
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(100px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default CookieConsent;
